/**
 * M10 — Log de Eventos (GUPM-139)
 * --------------------------------
 * Feed operacional legível ("MSE-03 atingiu nível ALERTA"), paridade com o
 * protótipo canônico v5.4.4. Responde "o que aconteceu na barragem" — o audit
 * trail técnico ("quem mexeu no quê") migrou pra aba /auditoria (GUPM-138).
 *
 * Fonte = datasource `eventos` (compute-on-read): a UI não sabe de onde vêm
 * os dados nem chama Supabase direto.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataSources } from '@/services/datasources';
import { eventosApp } from '@/services/datasources/eventos-app';
import {
  EVENT_FILTERS,
  EVENT_LABEL,
  eventsToCsv,
  filterEvents,
  type EventFilter,
  type EventType,
  type LogEvent,
} from '@/domain/eventos';
import { Card } from '@/ui';
import { BannerArea } from '@/components/BannerArea';

const TYPE_DOT: Record<EventType, string> = {
  ALERTA: 'bg-orange-400',
  ATENCAO: 'bg-yellow-400',
  ANOMALIA: 'bg-red-500',
  SIRENE: 'bg-blue-500',
  SISTEMA: 'bg-slate-500',
  EMERGENCIA: 'bg-red-600',
};

const TYPE_TEXT: Record<EventType, string> = {
  ALERTA: 'text-orange-400',
  ATENCAO: 'text-yellow-400',
  ANOMALIA: 'text-red-400',
  SIRENE: 'text-blue-400',
  SISTEMA: 'text-slate-400',
  EMERGENCIA: 'text-red-500',
};

const ARQUIVO_CSV = 'geoguard_log_eventos.csv';

function downloadCsv(events: LogEvent[], filtro: EventFilter) {
  const blob = new Blob([eventsToCsv(events)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ARQUIVO_CSV;
  a.click();
  URL.revokeObjectURL(url);
  // GUPM-335: dado que sai do app deixa rastro — quem levou, quantas linhas,
  // com qual filtro.
  void eventosApp.registrar('exportacao_dados', {
    tela: 'log_eventos',
    arquivo: ARQUIVO_CSV,
    linhas: events.length,
    filtro,
  });
}

export default function M10Log() {
  const { eventos } = useDataSources();
  const [filter, setFilter] = useState<EventFilter>('TODOS');

  const { data: events = [], isLoading, error } = useQuery<LogEvent[], Error>({
    queryKey: ['log-eventos'],
    queryFn: () => eventos.listEvents(),
  });

  const filtered = useMemo(() => filterEvents(events, filter), [events, filter]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-bold text-white flex items-center gap-2">
          <span aria-hidden>📜</span> Log de Eventos
        </h1>
        <button
          onClick={() => downloadCsv(filtered, filter)}
          disabled={filtered.length === 0}
          className="text-[11px] px-2.5 py-1 rounded-md border border-jf-border text-jf-muted hover:text-white hover:border-jf-yellow disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          ⬇ CSV
        </button>
      </div>

      <BannerArea modulo="M10" />

      {/* Pills de filtro */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {EVENT_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
              filter === f
                ? 'bg-jf-yellow text-black border-jf-yellow'
                : 'bg-transparent text-jf-muted border-jf-border hover:text-white'
            }`}
          >
            {EVENT_LABEL[f]}
          </button>
        ))}
      </div>

      {isLoading && <Card><p className="text-jf-muted">Carregando eventos…</p></Card>}

      {error && (
        <Card>
          <p role="alert" className="text-level-emergencia font-bold">Erro: {error.message}</p>
        </Card>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <Card>
          <p className="text-jf-muted">
            {events.length === 0
              ? 'Sem eventos ainda. O feed é derivado de leituras em nível não-normal (ATENÇÃO/ALERTA/EMERGÊNCIA) e de trocas de plantão.'
              : `Nenhum evento do tipo ${EVENT_LABEL[filter]} no período.`}
          </p>
        </Card>
      )}

      {filtered.length > 0 && (
        <ul className="space-y-1.5">
          {filtered.map((ev) => (
            <li
              key={ev.id}
              className="flex gap-2.5 p-2.5 rounded-lg border border-jf-border bg-jf-surface"
            >
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${TYPE_DOT[ev.type]}`} aria-hidden />
              <div className="flex-1 min-w-0">
                <p className="text-xs leading-relaxed text-white">{ev.msg}</p>
                <div className="flex flex-wrap gap-2 mt-1 text-[10px] text-jf-dim">
                  <span>{ev.ts.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <span className={`font-bold ${TYPE_TEXT[ev.type]}`}>{EVENT_LABEL[ev.type]}</span>
                  {ev.inst && <span className="font-mono text-jf-muted">{ev.inst}</span>}
                  {ev.tarp && <span className="font-mono text-jf-yellow">{ev.tarp}</span>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
